import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Stack, Tab, Tabs,
  Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  ENDODONTIC_STATUSES,
  IMPLANT_STATUSES,
  ORTHODONTIC_STATUSES,
  getStatusOption,
  getToothName,
} from '../../constants/odontology';
import type { DentalImplant, EndodonticTreatment, OrthodonticTreatment } from '../../types/odontology';
import {
  deleteDentalImplant,
  deleteEndodonticTreatment,
  deleteOrthodonticTreatment,
  listDentalImplants,
  listEndodonticTreatments,
  listOrthodonticTreatments,
} from '../../services/odontologyService';
import DentalImplantDialog from './DentalImplantDialog';
import EndodonticTreatmentDialog from './EndodonticTreatmentDialog';
import OrthodonticTreatmentDialog from './OrthodonticTreatmentDialog';
import { formatCurrencyBr, formatDateBr } from './odontologyHooks';

export interface PatientDentalTreatmentsTabProps {
  patientId: string;
  unitId?: string | null;
  linkedProfessionalId?: string | null;
}

type TreatmentTab = 'endodontics' | 'implants' | 'orthodontics';

const TABS: { value: TreatmentTab; label: string }[] = [
  { value: 'endodontics', label: 'Endodontia' },
  { value: 'implants', label: 'Implantes' },
  { value: 'orthodontics', label: 'Ortodontia' },
];

const TreatmentCard: React.FC<{
  title: string;
  subtitle: string;
  detail?: string | null;
  statusLabel: string;
  statusColor: string;
  statusBg: string;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ title, subtitle, detail, statusLabel, statusColor, statusBg, onEdit, onDelete }) => (
  <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F766E' }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>{subtitle}</Typography>
          {detail && (
            <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>{detail}</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Chip
            size="small"
            label={statusLabel}
            sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600, bgcolor: statusBg, color: statusColor }}
          />
          <Tooltip title="Editar">
            <IconButton size="small" onClick={onEdit} sx={{ color: '#0D9488' }} aria-label="Editar">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover">
            <IconButton size="small" onClick={onDelete} sx={{ color: '#94A3B8' }} aria-label="Remover">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const PatientDentalTreatmentsTab: React.FC<PatientDentalTreatmentsTabProps> = ({
  patientId,
  unitId,
  linkedProfessionalId,
}) => {
  const [tab, setTab] = useState<TreatmentTab>('endodontics');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [endodontics, setEndodontics] = useState<EndodonticTreatment[]>([]);
  const [implants, setImplants] = useState<DentalImplant[]>([]);
  const [orthodontics, setOrthodontics] = useState<OrthodonticTreatment[]>([]);

  const [endoDialog, setEndoDialog] = useState<{ open: boolean; item: EndodonticTreatment | null }>({ open: false, item: null });
  const [implantDialog, setImplantDialog] = useState<{ open: boolean; item: DentalImplant | null }>({ open: false, item: null });
  const [orthoDialog, setOrthoDialog] = useState<{ open: boolean; item: OrthodonticTreatment | null }>({ open: false, item: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { patient_id: patientId, unit_id: unitId || undefined, limit: 100 };
    try {
      const [endo, implant, ortho] = await Promise.all([
        listEndodonticTreatments(params),
        listDentalImplants(params),
        listOrthodonticTreatments(params),
      ]);
      setEndodontics(endo?.data || []);
      setImplants(implant?.data || []);
      setOrthodontics(ortho?.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar tratamentos odontológicos');
    } finally {
      setLoading(false);
    }
  }, [patientId, unitId]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async (message: string, action: () => Promise<void>) => {
    if (!window.confirm(message)) return;
    try {
      await action();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao remover registro');
    }
  };

  const addLabel: Record<TreatmentTab, string> = {
    endodontics: 'Novo tratamento de canal',
    implants: 'Novo implante',
    orthodontics: 'Novo tratamento ortodôntico',
  };

  const handleAdd = () => {
    if (tab === 'endodontics') setEndoDialog({ open: true, item: null });
    if (tab === 'implants') setImplantDialog({ open: true, item: null });
    if (tab === 'orthodontics') setOrthoDialog({ open: true, item: null });
  };

  const renderEndodontics = () => (
    !endodontics.length ? (
      <Alert severity="info" sx={{ borderRadius: 2 }}>Nenhum tratamento endodôntico registrado.</Alert>
    ) : (
      <Stack spacing={1.5}>
        {endodontics.map((item) => {
          const status = getStatusOption(ENDODONTIC_STATUSES, item.status);
          return (
            <TreatmentCard
              key={item.id}
              title={`Dente ${item.tooth_number} — ${getToothName(item.tooth_number)}`}
              subtitle={[
                item.canals?.length ? `${item.canals.length} canal(is)` : null,
                item.pulp_diagnosis,
                item.started_date ? `Início ${formatDateBr(item.started_date)}` : null,
                item.professional_name,
              ].filter(Boolean).join(' · ')}
              detail={item.canals?.length
                ? item.canals.map((c) => `${c.name}${c.working_length_mm ? ` ${c.working_length_mm} mm` : ''}`).join(' · ')
                : null}
              statusLabel={status.label}
              statusColor={status.color}
              statusBg={status.bg}
              onEdit={() => setEndoDialog({ open: true, item })}
              onDelete={() => confirmDelete(
                `Remover o tratamento endodôntico do dente ${item.tooth_number}?`,
                () => deleteEndodonticTreatment(item.id),
              )}
            />
          );
        })}
      </Stack>
    )
  );

  const renderImplants = () => (
    !implants.length ? (
      <Alert severity="info" sx={{ borderRadius: 2 }}>Nenhum implante registrado.</Alert>
    ) : (
      <Stack spacing={1.5}>
        {implants.map((item) => {
          const status = getStatusOption(IMPLANT_STATUSES, item.status);
          return (
            <TreatmentCard
              key={item.id}
              title={`Posição ${item.tooth_number}${item.brand ? ` — ${item.brand}` : ''}${item.model ? ` ${item.model}` : ''}`}
              subtitle={[
                item.diameter_mm && item.length_mm ? `${item.diameter_mm} × ${item.length_mm} mm` : null,
                item.platform,
                item.surgery_date ? `Cirurgia ${formatDateBr(item.surgery_date)}` : null,
                item.professional_name,
              ].filter(Boolean).join(' · ')}
              detail={[
                item.lot_number ? `Lote ${item.lot_number}` : null,
                item.torque_ncm ? `Torque ${item.torque_ncm} Ncm` : null,
                item.stability_isq ? `ISQ ${item.stability_isq}` : null,
                item.prosthesis_type,
              ].filter(Boolean).join(' · ') || null}
              statusLabel={status.label}
              statusColor={status.color}
              statusBg={status.bg}
              onEdit={() => setImplantDialog({ open: true, item })}
              onDelete={() => confirmDelete(
                `Remover o implante da posição ${item.tooth_number}?`,
                () => deleteDentalImplant(item.id),
              )}
            />
          );
        })}
      </Stack>
    )
  );

  const renderOrthodontics = () => (
    !orthodontics.length ? (
      <Alert severity="info" sx={{ borderRadius: 2 }}>Nenhum tratamento ortodôntico registrado.</Alert>
    ) : (
      <Stack spacing={1.5}>
        {orthodontics.map((item) => {
          const status = getStatusOption(ORTHODONTIC_STATUSES, item.status);
          return (
            <TreatmentCard
              key={item.id}
              title={item.appliance_type || 'Tratamento ortodôntico'}
              subtitle={[
                item.malocclusion_class ? `Classe ${item.malocclusion_class.replace('_', ' div. ')}` : null,
                item.started_date ? `Início ${formatDateBr(item.started_date)}` : null,
                Number(item.monthly_fee) > 0 ? `${formatCurrencyBr(item.monthly_fee)}/mês` : null,
                item.professional_name,
              ].filter(Boolean).join(' · ')}
              detail={item.activations?.length
                ? `${item.activations.length} ativação(ões) · última em ${formatDateBr(item.activations[item.activations.length - 1]?.date)}`
                : null}
              statusLabel={status.label}
              statusColor={status.color}
              statusBg={status.bg}
              onEdit={() => setOrthoDialog({ open: true, item })}
              onDelete={() => confirmDelete(
                'Remover este tratamento ortodôntico?',
                () => deleteOrthodonticTreatment(item.id),
              )}
            />
          );
        })}
      </Stack>
    )
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', letterSpacing: '-0.02em' }}>
          Tratamentos odontológicos
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
        >
          {addLabel[tab]}
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          mb: 2,
          borderBottom: '1px solid #E2E8F0',
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' },
        }}
      >
        {TABS.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          {tab === 'endodontics' && renderEndodontics()}
          {tab === 'implants' && renderImplants()}
          {tab === 'orthodontics' && renderOrthodontics()}
        </>
      )}

      <EndodonticTreatmentDialog
        open={endoDialog.open}
        onClose={() => setEndoDialog({ open: false, item: null })}
        patientId={patientId}
        unitId={unitId}
        treatment={endoDialog.item}
        defaultProfessionalId={linkedProfessionalId}
        onSaved={load}
      />

      <DentalImplantDialog
        open={implantDialog.open}
        onClose={() => setImplantDialog({ open: false, item: null })}
        patientId={patientId}
        unitId={unitId}
        implant={implantDialog.item}
        defaultProfessionalId={linkedProfessionalId}
        onSaved={load}
      />

      <OrthodonticTreatmentDialog
        open={orthoDialog.open}
        onClose={() => setOrthoDialog({ open: false, item: null })}
        patientId={patientId}
        unitId={unitId}
        treatment={orthoDialog.item}
        defaultProfessionalId={linkedProfessionalId}
        onSaved={load}
      />
    </Box>
  );
};

export default PatientDentalTreatmentsTab;
